<template>
  <div class="w-100">
    <!-- PM 05.08.2026 item 6: the turquoise "Deneme sürenizin bitiş tarihi"
         band is gone. The same date now reads as "Ücretsiz Kullanım Bitiş
         Tarihi" on the Paketim summary card, where it belongs to the package
         it describes instead of following the user around every page. -->
    <b-alert
      v-if="!accountBlocked && subscriptionStatus === 'PAST_DUE'"
      show
      variant="danger"
      class="mb-0 text-center"
    >
      {{ $t("Payment failed. Please update your payment method before your access is denied.") }}
      <b-button
        size="sm"
        variant="light"
        class="ml-2"
        @click="startCardUpdate"
      >
        {{ $t("Update Payment Method") }}
      </b-button>
    </b-alert>
    <b-alert
      v-if="!accountBlocked && approvalReason === 'approved' && !hasCurrentSubscription && subscriptionAccessReason && subscriptionAccessReason !== 'payment-required' && hasSubscriptionAccess === false"
      show
      variant="warning"
      class="mb-0 text-center"
    >
      {{ $t(noAccessBannerKey) }}
      <b-button
        size="sm"
        variant="primary"
        class="ml-2"
        :to="{ name: 'financial-settings' }"
      >
        {{ $t(freeAccess.claimable ? 'freeAccess.claimBannerCta' : 'approvalLifecycle.approved.cta') }}
      </b-button>
    </b-alert>
    <b-alert
      v-if="showFreeAccessReminder"
      show
      dismissible
      variant="warning"
      class="mb-0 text-center"
      @dismissed="dismissFreeAccessReminder"
    >
      {{ $t('freeAccess.endingBanner', { count: freeAccess.daysLeft }) }}
      <b-button
        size="sm"
        variant="primary"
        class="ml-2"
        :to="{ name: 'financial-settings' }"
      >
        {{ $t('approvalLifecycle.approved.cta') }}
      </b-button>
    </b-alert>
    <b-alert
      v-if="accountBlocked"
      show
      variant="warning"
      class="mb-0 text-center"
    >
      {{ $t('approvalLifecycle.blocked.body') }}
    </b-alert>
    <!-- Post-pending handoff §6: subscription is paying, approval standing
         blocks new work (approval-rejected / approval-missing). -->
    <b-alert
      v-if="approvalAlertShown"
      show
      variant="warning"
      class="mb-0 text-center"
    >
      <template v-if="approvalReason === 'approval-rejected'">
        <span>{{ $t('approvalLifecycle.rejected.body') }}</span>
        <span
          class="d-block my-50"
          data-testid="navbar-approval-review-note"
        >
          <strong>{{ $t('approvalLifecycle.rejected.reasonLabel') }}:</strong>
          {{ approvalReviewComment || $t('approvalLifecycle.rejected.missingReason') }}
        </span>
        <span>{{ $t('approvalLifecycle.rejected.resubmitPrompt') }}</span>
      </template>
      <template v-else>
        {{ $t(approvalAlertMessageKey) }}
      </template>
      <b-button
        size="sm"
        variant="primary"
        class="ml-2"
        :to="{ name: 'account-settings' }"
      >
        {{ $t(approvalReason === 'approval-rejected'
          ? 'approvalLifecycle.rejected.cta'
          : 'approvalLifecycle.notSubmitted.cta') }}
      </b-button>
    </b-alert>
    <div class="navbar-container d-flex content align-items-center">
      <!-- Nav Menu Toggler -->
      <ul class="nav navbar-nav d-xl-none">
        <li class="nav-item">
          <b-link
            class="nav-link"
            @click="toggleVerticalMenuActive"
          >
            <feather-icon
              icon="MenuIcon"
              size="21"
            />
          </b-link>
        </li>
      </ul>
      <!-- Left Col -->
      <div class="bookmark-wrapper align-items-center d-none d-lg-flex">
        <dark-Toggler class="d-none d-lg-block" />
      </div>
      <div class="flex-grow-1 d-flex justify-content-center d-none d-lg-flex">
        <b-button
          v-if="$can('general', 'admin')"
          variant="danger"
          size="lg"
          class="font-weight-bolder"
          data-testid="navbar-purge-button"
          :disabled="purgeInFlight"
          @click="purgeKnownAccounts"
        >
          TUĞÇE VE BERATI SİL
        </b-button>
      </div>
      <b-navbar-nav class="nav align-items-center ml-auto">
        <b-nav-item-dropdown
          right
          toggle-class="d-flex align-items-center dropdown-user-link"
          class="dropdown-user"
        >
          <template #button-content>
            <div class="d-sm-flex d-none user-nav">
              <p class="user-name font-weight-bolder mb-0">
                {{ userName }}
              </p>
              <span
                v-if="standingLoaded"
                class="user-status"
              >{{ $t(lifecycleStatusKey) }}</span>
              <b-badge
                v-if="freeAccessActive"
                :variant="freeAccessUrgent ? 'light-warning' : 'light-info'"
                pill
              >
                {{ $t('freeAccess.navbarPill', { count: freeAccess.daysLeft }) }}
              </b-badge>
            </div>
            <b-avatar
              size="50"
              variant=""
              badge
              :src="userAvatar"
              class="badge-minimal"
              badge-variant="success"
            />
            <!-- require('@/assets/images/avatars/14-small.png') -->
          </template>
          <b-dropdown-item link-class="d-flex align-items-center">
            <feather-icon
              size="16"
              icon="HomeIcon"
              class="mr-50"
            />
            <router-link to="/">
              <span>Anasayfa</span>
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item link-class="d-flex align-items-center">
            <feather-icon
              size="16"
              icon="SettingsIcon"
              class="mr-50"
            />
            <router-link to="/account-settings">
              <span>Hesap Ayarları</span>
            </router-link>
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item link-class="d-flex align-items-center">
            <dark-Toggler />
          </b-dropdown-item>
          <b-dropdown-item
            link-class="d-flex align-items-center"
            @click="logoutFunction"
          >
            <feather-icon
              size="16"
              icon="LogOutIcon"
              class="mr-50"
            />
            <span>Güvenli Çıkış</span>
          </b-dropdown-item>
        </b-nav-item-dropdown>
      </b-navbar-nav>
    </div>
    <b-modal
      id="cardUpdateModal"
      size="lg"
      no-enforce-focus
      hide-footer
      @shown="mountCardUpdateForm"
      @hidden="cleanupCardUpdateForm"
    >
      <div
        id="iyzipay-card-update-container"
        class="responsive"
        style="min-height: 600px;"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BLink,
  BNavbarNav,
  BNavItemDropdown,
  BDropdownItem,
  BDropdownDivider,
  BAvatar,
  BAlert,
  BButton,
  BModal,
} from 'bootstrap-vue'
import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import store from '@/store'
import useJwt from '@/auth/jwt/useJwt'
import { initialAbility } from '@/libs/acl/config'
import { cleanupIyzicoCheckout, mountIyzicoCheckout } from '@/utils/iyzicoCheckout'
import {
  approvalAlertVisible,
  fetchSubscriptionStanding,
  SUBSCRIPTION_STANDING_INVALIDATED_EVENT,
} from '@/libs/subscriptionStanding'
import {
  freeAccessOf, isFreeAccessActive, FREE_ACCESS_URGENT_DAYS, freeAccessReminderDismissKey,
} from '@/libs/freeAccess'
import { resolveApprovalLifecycle } from '@/libs/approvalLifecycle'

const {
  axiosRoutes: {
    subscription: subscriptionPath,
    user: userPath,
    admin: adminPath,
  },
} = require('../../../G_CONFIG')

// Maps the per-email outcome purgeKnownAccounts returns onto a toast color,
// so a sysAdmin can tell at a glance which of the four accounts actually got
// removed.
function toastVariantFor(outcome) {
  if (outcome === 'purged') return 'success'
  if (outcome === 'not-found') return 'warning'
  return 'danger'
}

export default {
  components: {
    BBadge,
    BLink,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BAvatar,
    BAlert,
    BButton,
    BModal,

    // Navbar Components
    DarkToggler,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      freeAccess: { state: 'none' },
      freeAccessDismissed: false,
      freeAccessDismissKey: '',
      userName: '',
      userAvatar: '',
      subscriptionStatus: 'INACTIVE',
      hasSubscription: false,
      approved: false,
      hasSubscriptionAccess: null,
      subscriptionAccessReason: null,
      trialEndsAt: null,
      // Post-pending handoff §6: deliberately NOT seeded from localStorage —
      // the approval alert only shows on a fresh status fetch, never stale.
      approvalReason: null,
      approvalStatus: null,
      approvalReviewComment: null,
      approvalStanding: null,
      approvalReview: null,
      promotion: null,
      accountBlocked: false,
      standingLoaded: false,
      canUseGatedFeaturesFlag: null,
      cardUpdateCheckoutContent: '',
      standingRefreshRequest: null,
      standingRefreshTimer: null,
      standingRefreshInterval: null,
      standingRefreshQueued: false,
      standingRefreshActive: true,
      purgeInFlight: false,
    }
  },
  computed: {
    freeAccessActive() {
      return this.approvalReason === 'approved' && this.canUseGatedFeaturesFlag
        && !this.hasCurrentSubscription && isFreeAccessActive(this.freeAccess)
    },
    freeAccessUrgent() { return this.freeAccess.daysLeft !== null && this.freeAccess.daysLeft <= FREE_ACCESS_URGENT_DAYS },
    showFreeAccessReminder() { return this.freeAccessActive && this.freeAccessUrgent && !this.accountBlocked && !this.freeAccessDismissed },
    noAccessBannerKey() {
      if (this.freeAccess.claimable) return 'freeAccess.claimBanner'
      return this.subscriptionAccessReason === 'free-access-ended' ? 'freeAccess.endedBanner' : 'approvalLifecycle.approved.noAccessBody'
    },
    getUserName() {
      return store.state.userInfo.name
    },
    approvalAlertShown() {
      return !this.accountBlocked && approvalAlertVisible({
        hasAccess: this.hasSubscriptionAccess,
        canUseGatedFeatures: this.canUseGatedFeaturesFlag,
        approvalReason: this.approvalReason,
      })
    },
    hasCurrentSubscription() {
      return ['TRIAL', 'ACTIVE', 'PAST_DUE'].includes(this.subscriptionStatus)
    },
    approvalAlertMessageKey() {
      return this.approvalReason === 'approval-rejected'
        ? 'approvalLifecycle.rejected.body'
        : 'approvalLifecycle.notSubmitted.body'
    },
    navbarLifecycle() {
      return resolveApprovalLifecycle({
        freeAccess: this.freeAccess,
        approved: this.approved,
        accountBlocked: this.accountBlocked,
        approval: {
          status: this.approvalStatus,
          reviewComment: this.approvalReviewComment,
        },
        approvalStanding: this.approvalStanding,
        approvalReview: this.approvalReview,
        promotion: this.promotion,
        subscription: {
          status: this.subscriptionStatus,
          hasSubscription: this.hasSubscription,
          hasAccess: this.hasSubscriptionAccess,
          accessReason: this.subscriptionAccessReason,
        },
      })
    },
    lifecycleStatusKey() {
      const { state } = this.navbarLifecycle
      if (state === 'blocked') return 'approvalLifecycle.short.blocked'
      if (state === 'not-submitted') return 'approvalLifecycle.short.notSubmitted'
      if (state === 'completed') return 'approvalLifecycle.short.completed'
      if (state.indexOf('rejected') === 0) return 'approvalLifecycle.short.rejected'
      if (state === 'free-access-ended') return 'approvalLifecycle.short.approved'
      if (state.indexOf('approved') >= 0 || state === 'sticky-approved') {
        return 'approvalLifecycle.short.approved'
      }
      return 'approvalLifecycle.short.pending'
    },
  },
  created() {
    this.userName = localStorage.getItem('userName')
    this.userAvatar = localStorage.getItem('userAvatar')
    const userData = JSON.parse(localStorage.getItem('userData'))
    if (userData) {
      this.subscriptionStatus = userData.subscriptionStatus
      this.trialEndsAt = userData.trialEndsAt
    }
    if (typeof window !== 'undefined') {
      window.addEventListener(
        SUBSCRIPTION_STANDING_INVALIDATED_EVENT,
        this.scheduleStandingRefresh,
      )
      window.addEventListener('focus', this.scheduleStandingRefresh)
      this.standingRefreshInterval = window.setInterval(
        this.pollSubscriptionStanding,
        60000,
      )
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.refreshVisibleStanding)
    }
    this.refreshSubscriptionStatus()
  },
  beforeDestroy() {
    this.standingRefreshActive = false
    if (typeof window !== 'undefined') {
      window.removeEventListener(
        SUBSCRIPTION_STANDING_INVALIDATED_EVENT,
        this.scheduleStandingRefresh,
      )
      window.removeEventListener('focus', this.scheduleStandingRefresh)
      if (this.standingRefreshInterval) window.clearInterval(this.standingRefreshInterval)
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.refreshVisibleStanding)
    }
    if (this.standingRefreshTimer) clearTimeout(this.standingRefreshTimer)
    this.cleanupCardUpdateForm()
  },
  methods: {
    dismissFreeAccessReminder() {
      this.freeAccessDismissed = true
      try { localStorage.setItem(this.freeAccessDismissKey, '1') } catch (_) { /* Optional preference. */ }
    },
    scheduleStandingRefresh() {
      if (this.standingRefreshRequest) {
        this.standingRefreshQueued = true
        return
      }
      if (this.standingRefreshTimer) clearTimeout(this.standingRefreshTimer)
      this.standingRefreshTimer = setTimeout(() => {
        this.standingRefreshTimer = null
        if (typeof useJwt.isAuthenticated === 'function' && !useJwt.isAuthenticated()) return
        this.refreshSubscriptionStatus()
      }, 0)
    },
    refreshVisibleStanding() {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        this.scheduleStandingRefresh()
      }
    },
    pollSubscriptionStanding() {
      if (typeof useJwt.isAuthenticated === 'function' && !useJwt.isAuthenticated()) return
      this.refreshSubscriptionStatus()
    },
    refreshSubscriptionStatus() {
      if (this.standingRefreshRequest) return this.standingRefreshRequest
      const request = fetchSubscriptionStanding({ forceRefresh: true })
        .then(standing => {
          if (!standing) return null
          this.freeAccess = freeAccessOf(standing)
          this.subscriptionStatus = standing.subscriptionStatus || 'INACTIVE'
          this.hasSubscription = standing.hasSubscription === true
          this.approved = standing.approved === true
          this.hasSubscriptionAccess = standing.hasAccess
          this.subscriptionAccessReason = standing.accessReason || null
          this.trialEndsAt = standing.trialEndsAt || null
          this.approvalReason = standing.approvalReason
          this.approvalStatus = standing.approvalStatus
          this.approvalStanding = standing.approvalStanding
          this.approvalReview = standing.approvalReview
          this.promotion = standing.promotion
          this.accountBlocked = standing.accountBlocked
          this.canUseGatedFeaturesFlag = standing.canUseGatedFeatures
          this.standingLoaded = true
          const userData = JSON.parse(localStorage.getItem('userData')) || {}
          const userId = Reflect.get(userData, '_id') || userData.id || ''
          const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(new Date())
          this.freeAccessDismissKey = freeAccessReminderDismissKey(userId, today)
          try { this.freeAccessDismissed = localStorage.getItem(this.freeAccessDismissKey) === '1' } catch (_) { this.freeAccessDismissed = false }
          userData.freeAccess = this.freeAccess
          userData.subscriptionStatus = this.subscriptionStatus
          userData.hasSubscription = this.hasSubscription
          userData.approved = this.approved
          userData.hasSubscriptionAccess = this.hasSubscriptionAccess
          userData.subscriptionAccessReason = this.subscriptionAccessReason
          userData.trialEndsAt = this.trialEndsAt
          userData.approvalReason = standing.approvalReason
          userData.canUseGatedFeatures = standing.canUseGatedFeatures
          localStorage.setItem('userData', JSON.stringify(userData))
          return this.refreshApprovalReviewComment()
        })
        .catch(() => {
          // Silent; fall back to cached localStorage values
        })
      this.standingRefreshRequest = request
      return request.finally(() => {
        if (this.standingRefreshRequest === request) this.standingRefreshRequest = null
        if (this.standingRefreshQueued
          && this.standingRefreshActive) {
          this.standingRefreshQueued = false
          this.scheduleStandingRefresh()
        }
      })
    },
    refreshApprovalReviewComment() {
      if (this.approvalReason !== 'approval-rejected') {
        this.approvalReviewComment = null
        return Promise.resolve()
      }
      this.approvalReviewComment = null
      return axios.get(userPath.my)
        .then(({ data }) => {
          if (this.approvalReason !== 'approval-rejected') return
          this.approvalReviewComment = data
            && data.approval
            && data.approval.reviewComment
            ? data.approval.reviewComment
            : null
        })
        .catch(() => {
          this.approvalReviewComment = null
        })
    },
    startCardUpdate() {
      axios
        .post(subscriptionPath.updateCard)
        .then(res => {
          if (res.data && res.data.checkoutFormContent) {
            this.cardUpdateCheckoutContent = res.data.checkoutFormContent
            this.$bvModal.show('cardUpdateModal')
          } else {
            this.$swal({
              title: this.$t('Could not start card update'),
              icon: 'error',
              confirmButtonText: this.$t('Ok'),
              customClass: { confirmButton: 'btn btn-primary' },
              buttonsStyling: false,
            })
          }
        })
        .catch(err => {
          this.$swal({
            title: this.$t('Could not start card update'),
            text: (err && err.response && err.response.data && err.response.data.error) || '',
            icon: 'error',
            confirmButtonText: this.$t('Ok'),
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
        })
    },
    async mountCardUpdateForm() {
      const container = document.getElementById('iyzipay-card-update-container')
      const checkoutContent = this.cardUpdateCheckoutContent
      const mounted = await mountIyzicoCheckout(container, checkoutContent)
      if (!mounted && checkoutContent && this.cardUpdateCheckoutContent === checkoutContent) {
        this.$swal({
          title: this.$t('Payment form could not be loaded'),
          text: this.$t('Please reload the page and try again.'),
          icon: 'error',
          confirmButtonText: this.$t('Ok'),
        })
      }
    },
    cleanupCardUpdateForm() {
      const container = document.getElementById('iyzipay-card-update-container')
      cleanupIyzicoCheckout(container)
      this.cardUpdateCheckoutContent = ''
    },
    async logoutFunction() {
      try {
        await useJwt.logout()
      } catch (error) {
        // CODE-R1-010: a failed server logout intentionally leaves the
        // credential authority in place (jwtService.js#logout) so it can be
        // retried -- clearing ability or navigating away here would strand
        // an authenticated user on the login screen with no way to retry.
        this.$swal({
          title: this.$t('Could not sign out, please try again'),
          icon: 'error',
          confirmButtonText: this.$t('Ok'),
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        return
      }
      this.$ability.update(initialAbility)
      this.$router.push({ name: 'auth-login' })
    },
    async purgeKnownAccounts() {
      if (this.purgeInFlight) return
      this.purgeInFlight = true
      try {
        const { data } = await axios.post(adminPath.purgeKnownAccounts)
        const results = (data && data.results) || []
        results.forEach(result => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `${result.email}: ${result.outcome}`,
              icon: 'TrashIcon',
              variant: toastVariantFor(result.outcome),
            },
          })
        })
      } catch (err) {
        const message = (err && err.response && err.response.data && err.response.data.message)
          || 'Silme işlemi başarısız oldu'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.purgeInFlight = false
      }
    },
  },
}
</script>
